import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Alert,
  Box,
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  MenuItem,
  Skeleton,
  Stack,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Paper,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { Home as HomeIcon, NavigateNext as NavigateNextIcon } from '@mui/icons-material'

import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { PageHelpPopover } from '@/components/common/PageHelpPopover'
import { ExerciseRenderer } from '@/components/exercises/renderers/ExerciseRenderer'
import {
  PROFILE_LEVELS,
  PROFILE_SKILLS,
  languageProfileApi,
  placementApi,
  type PlacementRun,
  type PlacementRunSummary,
  type PlacementRunItem,
  type ProfileLevel,
  type SkillMeasurement,
  type SelfAssessmentPayload,
  type StudentLanguageProfile,
} from '@/api/placement'
import { useAuthStore } from '@/stores/authStore'
import { ROUTES } from '@/utils/constants'
import { displayErrorMessage, getErrorMessage } from '@/utils/error'
import { formatDateDMY } from '@/utils/date'
import { PROBE_SKILLS, skillMeasurementLabel } from '@/utils/placementLabels'

/** Порядок ступеней для шагового индикатора. */
/**
 * Ответ заполнен, а не просто заведён пустым объектом.
 *
 * Рендерер кладёт ключ в ответ уже при первом касании поля, поэтому проверяем
 * значения: пустая строка, пустой список и пустая карта — это «не отвечено».
 */
function isFilled(answer: Record<string, unknown> | undefined): boolean {
  if (answer === undefined) return false

  return Object.values(answer).some((value) => {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0
    if (typeof value === 'object') return Object.keys(value as object).length > 0

    return true
  })
}

function draftKeyFor(runId: number, blockNumber: number): string {
  return `placement-draft-${runId}-${blockNumber}`
}

function readDraft(runId: number, blockNumber: number): Record<number, Record<string, unknown>> | null {
  try {
    const raw = window.localStorage.getItem(draftKeyFor(runId, blockNumber))

    return raw === null ? null : (JSON.parse(raw) as Record<number, Record<string, unknown>>)
  } catch {
    return null
  }
}

function clearDraft(key: string | null): void {
  if (key === null) return

  try {
    window.localStorage.removeItem(key)
  } catch {
    // Хранилище недоступно — черновика и не было.
  }
}

/**
 * Убрать черновики чужих прогонов и прошлых блоков.
 *
 * Ключ содержит номер прогона и блока, поэтому без уборки хранилище копит по
 * записи на каждый пройденный блок и каждую пересдачу.
 */
function sweepDrafts(keep: string | null): void {
  try {
    Object.keys(window.localStorage)
      .filter((key) => key.startsWith('placement-draft-') && key !== keep)
      .forEach((key) => window.localStorage.removeItem(key))
  } catch {
    // Хранилище недоступно — убирать нечего.
  }
}

const STAGES = ['goals', 'adaptive', 'probes'] as const

/**
 * Первая ступень спрашивает только самооценку.
 *
 * Цель, детали и желаемое расписание переехали в анкету ученика: они не
 * зависят от уровня, меняются в любой момент и нужны учителю независимо от
 * теста. Спрашивать их здесь значило запирать тест за анкетой, которую ученик
 * пришёл пропустить, и заново переспрашивать на каждой пересдаче.
 */
type GoalsForm = {
  selfLevel: ProfileLevel | ''
}

const EMPTY_GOALS: GoalsForm = {
  selfLevel: '',
}

/**
 * Прохождение теста уровня учеником.
 *
 * Три ступени по возрастанию точности: анкета, адаптивный блок закрытых
 * заданий, пробы письма и говорения. Уйти можно на любой: выход закрывает тест
 * и переносит измеренное в профиль — но и запирает пересдачу на месяц, как
 * завершение, поэтому спрашивается подтверждение.
 *
 * Промежуточный результат ученику НЕ показывается: узнав «пока B1», он меняет
 * поведение, и следующий блок померит уже не язык. Уровни появляются только
 * после завершения прогона.
 */
/**
 * «Это не экзамен» — второй акцент экрана после самого уровня.
 *
 * Тон важнее размера: ученик, прочитавший цифру как оценку, идёт спорить с
 * ней вместо того, чтобы заниматься. Мелкой сноской это не читалось.
 */
function FairPlayNote() {
  const { t } = useTranslation()

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        borderWidth: 2,
        borderColor: 'info.main',
        bgcolor: (theme) => alpha(theme.palette.info.main, 0.06),
      }}
    >
      <Typography variant="h6" gutterBottom>
        {t('placement.fairPlayTitle')}
      </Typography>
      <Typography variant="body1">{t('placement.fairPlay')}</Typography>
    </Paper>
  )
}

export function PlacementTestPage() {
  const { t } = useTranslation()
  const currentUser = useAuthStore((state) => state.user)

  const [run, setRun] = useState<PlacementRun | null>(null)
  /** Уровни из профиля: экран результата иначе живёт только до перезагрузки. */
  const [profile, setProfile] = useState<StudentLanguageProfile | null>(null)
  const [retestAt, setRetestAt] = useState<string | null>(null)
  /** Последний закрытый прогон: из него берётся счёт «решено верно». */
  const [lastRun, setLastRun] = useState<PlacementRunSummary | null>(null)
  const [items, setItems] = useState<PlacementRunItem[]>([])
  const [answers, setAnswers] = useState<Record<number, Record<string, unknown>>>({})
  const [goals, setGoals] = useState<GoalsForm>(EMPTY_GOALS)

  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [leaveOpen, setLeaveOpen] = useState(false)

  /**
   * Ответы блока живут на клиенте до общей сдачи, поэтому дублируются в
   * браузер: перезагрузка вкладки посреди блока иначе стирает всё, что человек
   * уже написал, и заставляет решать заново.
   */
  const draftKey = run === null ? null : `placement-draft-${run.id}-${run.block_number}`

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const state = await placementApi.state()

      // Черновик читается ДО того, как прогон попадёт в состояние: ключ
      // черновика собирается из прогона, и эффект сохранения, сработав на
      // пустых ответах, затёр бы его раньше, чем мы успели прочитать.
      const saved = state.run === null
        ? null
        : readDraft(state.run.id, state.run.block_number)

      sweepDrafts(
        state.run === null ? null : draftKeyFor(state.run.id, state.run.block_number),
      )

      setAnswers(saved ?? {})
      setRun(state.run)
      setRetestAt(state.retest_available_at)

      // Условие по статусу, а не по ступени: протухший прогон возвращается со
      // своей ступенью и статусом «брошен», и запрос блока по нему встречал
      // вернувшегося через неделю ученика красной ошибкой вместо кнопки
      // «Начать тест».
      if (
        state.run?.status === 'in_progress'
        && (state.run.stage === 'adaptive' || state.run.stage === 'probes')
      ) {
        // Ошибка выдачи блока не глотается: пустой список неотличим от
        // «заданий нет», и поломка сервера выглядела бы штатным экраном.
        setItems(await placementApi.block(state.run.id))
        setProfile(null)
      } else {
        setItems([])

        // Вне прохождения показываем измеренное из профиля: экран результата
        // держался на ответе одного запроса и пропадал при перезагрузке, а
        // своей страницы профиля у ученика нет.
        if (currentUser !== null) {
          const [profileRow, runRows] = await Promise.all([
            languageProfileApi.show(currentUser.id).catch(() => null),
            // Счёт по навыкам живёт в прогоне, а закрытый прогон в «текущих»
            // не возвращается — после перезагрузки строка «решено верно»
            // исчезала вместе с ним, хотя объясняет она именно результат.
            languageProfileApi.runs(currentUser.id).catch(() => []),
          ])

          setProfile(profileRow)
          setLastRun(runRows[0] ?? null)
        }
      }
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setLoading(false)
    }
  }, [currentUser, t])

  useEffect(() => {
    void load()
  }, [load])

  useEffect(() => {
    if (draftKey === null) return

    try {
      window.localStorage.setItem(draftKey, JSON.stringify(answers))
    } catch {
      // Приватное окно или запрет на хранилище: черновик — подстраховка,
      // а не условие работы теста.
    }
  }, [answers, draftKey])

  const activeStage = run === null ? 0 : STAGES.indexOf(run.stage)

  /**
   * Уровни для экрана результата: свежие из прогона, иначе из профиля.
   *
   * Прогон отдаёт их только сразу после завершения, профиль — всегда; после
   * перезагрузки страницы виден именно профиль.
   */
  const measuredLevels = useMemo<Record<string, ProfileLevel | null> | null>(() => {
    if (run?.status === 'completed' && run.computed_levels !== null) {
      return run.computed_levels
    }

    return profile !== null && profile.has_level ? profile.skill_levels : null
  }, [profile, run])

  /** Разбор по навыкам: у свежего прогона свой, иначе из профиля. */
  const measurements = useMemo<Record<string, SkillMeasurement>>(
    () => run?.skill_measurements ?? profile?.skill_measurements ?? {},
    [profile, run],
  )

  /**
   * Счёт «верных из выданных» — только по этому прогону.
   *
   * В профиль он не переносится по замыслу: профиль отвечает на «какой уровень
   * у ученика», а не «как он сдавал в прошлый раз». Пустые навыки отсеиваются —
   * строка «0 из 0» ничего не сообщает.
   */
  const scoreRows = useMemo(
    () =>
      Object.entries(run?.skill_scores ?? lastRun?.skill_scores ?? {}).filter(
        ([, score]) => score.total > 0,
      ),
    [lastRun, run],
  )

  /**
   * Общий уровень: из завершённого прогона либо из профиля.
   *
   * Прогон отдаёт разбивку по навыкам, но не общий, поэтому у свежего
   * результата он считается тем же правилом, что и на сервере, — медианой
   * измеренных.
   */
  const overallLevel = useMemo<string | null>(() => {
    if (profile?.overall_level != null) return profile.overall_level

    const measured = Object.values(run?.computed_levels ?? {}).filter(
      (level): level is ProfileLevel => level !== null,
    )

    if (measured.length === 0) return null

    const ordered = [...measured].sort(
      (a, b) => PROFILE_LEVELS.indexOf(a) - PROFILE_LEVELS.indexOf(b),
    )

    // Медиана, при чётном числе — нижняя из двух средних: как в движке.
    return ordered[Math.floor((ordered.length - 1) / 2)]
  }, [profile, run])

  /**
   * Сколько заданий блока заполнено.
   *
   * Считается по ответам на клиенте, а не по отметке сервера: задания больше
   * не сдаются по одному — блок уходит целиком, и до этого момента сервер о
   * них ничего не знает.
   */
  const filledCount = useMemo(
    () => items.filter((item) => isFilled(answers[item.id])).length,
    [answers, items],
  )

  const withBusy = async (action: () => Promise<void>) => {
    setBusy(true)
    setError(null)
    try {
      await action()
    } catch (err) {
      setError(displayErrorMessage(getErrorMessage(err), t('common.error')))
    } finally {
      setBusy(false)
    }
  }

  const start = () =>
    withBusy(async () => {
      setRun(await placementApi.start())
    })

  const submitGoals = () =>
    withBusy(async () => {
      if (run === null) return

      const payload: SelfAssessmentPayload = {
        self_assessed_level: goals.selfLevel === '' ? null : goals.selfLevel,
      }

      const updated = await placementApi.submitSelfAssessment(run.id, payload)
      setRun(updated)
      setItems(await placementApi.block(updated.id))
      setAnswers({})
    })

  /**
   * Сдать блок целиком и пойти дальше.
   *
   * Одной кнопкой, а не по заданию: человек заполняет весь блок и идёт дальше,
   * а сдавать каждое задание отдельно он забывает — и упирался в «блок отвечен
   * не полностью», не понимая, какое именно задание недоделано.
   *
   * Уже сданные пропускаются: если сдача сорвалась на середине, повтор не
   * должен натыкаться на «попытка не активна» по первым заданиям.
   */
  const submitBlock = (then: () => Promise<void>) =>
    withBusy(async () => {
      for (const item of items) {
        if (item.answered) continue

        const updated = await placementApi.submitItem(item.id, answers[item.id] ?? {})

        setItems((current) => current.map((row) => (row.id === updated.id ? { ...row, ...updated } : row)))
      }

      clearDraft(draftKey)

      await then()
    })

  /** Спросить про незаполненные и сдать блок. */
  const finishBlock = (then: () => Promise<void>) => {
    const skipped = items.length - filledCount

    if (skipped > 0 && !window.confirm(t('placement.skippedConfirm', { count: skipped }))) {
      return
    }

    void submitBlock(then)
  }

  /**
   * Продвинуться после сдачи блока.
   *
   * Без обёртки в busy: её держит {@link submitBlock}, из которого этот шаг и
   * вызывается — блок и переход теперь одно действие.
   */
  const advanceAfterBlock = async () => {
    if (run === null) return

    const updated = await placementApi.advance(run.id)

    setRun(updated)
    setAnswers({})

    if (updated.status !== 'in_progress') {
      // Прогон закрылся сам: держим его же, а срок пересдачи спрашиваем
      // отдельно — перечитывание состояния вернуло бы пустоту вместо
      // результата, открытых прогонов там больше нет.
      setItems([])
      setRetestAt((await placementApi.state()).retest_available_at)

      return
    }

    // На пробах блок тот же запрос: сервер выдаёт письмо и говорение как
    // обычные позиции прогона.
    setItems(await placementApi.block(updated.id))
  }

  const completeAfterBlock = async () => {
    if (run === null) return

    // Закрытый прогон держим из ответа: список текущих его уже не отдаёт —
    // там только открытые, — а результат ученик видит только здесь, своей
    // страницы профиля у него нет.
    const finished = await placementApi.complete(run.id)

    setRun(finished)
    setItems([])
    setAnswers({})

    // Профиль нужен, чтобы не соврать: при правке учителя результат теста в
    // него не попадает, и «уровень записан» было бы неправдой.
    if (currentUser !== null) {
      setProfile(await languageProfileApi.show(currentUser.id).catch(() => null))
    }

    // Срок повторной сдачи считает сервер, и со старым значением экран
    // предлагал «Начать тест» сразу после завершения — клик отвечал ошибкой.
    setRetestAt((await placementApi.state()).retest_available_at)
  }

  /** Сохранить результат и уйти: сдать заполненное и закрыть прогон. */
  const saveAndLeave = () => {
    setLeaveOpen(false)

    void submitBlock(completeAfterBlock)
  }


  const abandon = () =>
    withBusy(async () => {
      if (run === null) return

      setLeaveOpen(false)
      clearDraft(draftKey)

      await placementApi.abandon(run.id)
      await load()
    })


  return (
    <DashboardLayout>
      <Box sx={{ p: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
          <Link
            to={ROUTES.DASHBOARD}
            style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'inherit' }}
          >
            <HomeIcon fontSize="small" />
            {t('dashboard.home')}
          </Link>
          <Typography color="text.primary">{t('placement.title')}</Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" fontWeight={700}>
              {t('placement.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('placement.subtitle')}
            </Typography>
          </Box>
          <PageHelpPopover pageKey="placement" />
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading && (
          <Card>
            <CardContent>
              <Skeleton variant="rounded" height={56} sx={{ mb: 2 }} />
              <Skeleton variant="rounded" height={180} />
            </CardContent>
          </Card>
        )}

        {!loading && run !== null && run.status === 'in_progress' && (
          <Stepper activeStep={activeStage} sx={{ mb: 3 }}>
            {STAGES.map((stage) => (
              <Step key={stage}>
                <StepLabel>{t(`placement.stages.${stage}`)}</StepLabel>
              </Step>
            ))}
          </Stepper>
        )}

        {/* Прогона нет: предлагаем начать либо объясняем, когда можно снова */}
        {!loading && (run === null || run.status !== 'in_progress') && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                {measuredLevels !== null && (
                  <>
                    {run?.status === 'completed' && (
                      <Alert severity="success">
                        {profile?.level_source === 'teacher'
                          ? t('placement.finishedTeacherKept')
                          : t('placement.finished')}
                      </Alert>
                    )}
                    {/* Общий уровень — главное, за чем ученик шёл: крупно и
                        первым, а разбивка по навыкам под ним */}
                    {overallLevel !== null && (
                      <Box>
                        <Typography variant="overline" color="text.secondary">
                          {t('placement.overallLevelTitle')}
                        </Typography>
                        <Typography variant="h2" fontWeight={700} lineHeight={1}>
                          {overallLevel}
                        </Typography>
                      </Box>
                    )}

                    {/* Вторым по величине после уровня: цифра без этой оговорки
                        читается как оценка, а тест её не выдаёт. */}
                    <FairPlayNote />

                    <Typography variant="subtitle2">{t('placement.yourLevels')}</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {PROFILE_SKILLS.map((skill) => {
                        const level = measuredLevels[skill]

                        return (
                          <Chip
                            key={skill}
                            label={`${t(`placement.skills.${skill}`)}: ${
                              level ?? skillMeasurementLabel(measurements[skill], t, { isProbe: PROBE_SKILLS.includes(skill) })
                            }`}
                            color={level ? 'primary' : 'default'}
                            variant={level ? 'filled' : 'outlined'}
                          />
                        )
                      })}
                    </Stack>

                    {/* Счёт по навыкам: ответ на «почему такой уровень». Какие
                        именно задания решены верно, ученику не показываем —
                        объяснять это ему не нужно, разбор его заданий лежит у
                        преподавателя. */}
                    {scoreRows.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2">{t('placement.yourScores')}</Typography>
                        <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                          {scoreRows.map(([skill, score]) => (
                            <Typography key={skill} variant="body2">
                              {t(`placement.skills.${skill}`)}:{' '}
                              {t('placement.scoreOf', { correct: score.correct, total: score.total })}
                            </Typography>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </>
                )}

                {run?.status === 'abandoned' && (
                  <Alert severity="info">
                    {retestAt === null ? t('placement.abandoned') : t('placement.abandonedLocked')}
                  </Alert>
                )}

                {/* Закрыт без единого измерения: без объяснения экран выглядел
                    бы как сброс в никуда. Признак берём из самого прогона —
                    срок пересдачи считается только по завершённым, и по нему
                    предупреждение показывалось бы всем брошенным подряд */}
                {run !== null
                  && run.status !== 'in_progress'
                  && run.computed_levels !== null
                  && Object.values(run.computed_levels).every((level) => level === null) && (
                  <Alert severity="warning">{t('placement.nothingMeasured')}</Alert>
                )}

                <Typography variant="body2" color="text.secondary">
                  {t('placement.intro')}
                </Typography>

                {/* До первого прогона уровня ещё нет, и оговорка идёт здесь —
                    выше она рисуется только рядом с измеренным уровнем. */}
                {measuredLevels === null && <FairPlayNote />}

                {retestAt !== null ? (
                  <Alert severity="info">
                    {t('placement.retestAt', { date: formatDateDMY(retestAt) })}
                  </Alert>
                ) : (
                  <Box>
                    <Button variant="contained" disabled={busy} onClick={() => void start()}>
                      {busy ? t('common.loading') : t('placement.start')}
                    </Button>
                  </Box>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Первая ступень: только самооценка */}
        {!loading && run?.status === 'in_progress' && run.stage === 'goals' && (
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="body2" color="text.secondary">
                  {t('placement.selfLevelIntro')}
                </Typography>

                {/* Прошлая самооценка и фактический результат — рядом с полем:
                    ученик, проходящий тест не впервые, видит, что о нём уже
                    известно, и не гадает, какое число называть. */}
                {(profile?.self_assessed_level != null || profile?.overall_level != null) && (
                  <Alert severity="info" icon={false}>
                    <Stack spacing={0.5}>
                      {profile?.self_assessed_level != null && (
                        <Typography variant="body2">
                          {t('placement.previousSelfAssessment', { level: profile.self_assessed_level })}
                        </Typography>
                      )}
                      {profile?.overall_level != null && (
                        <Typography variant="body2">
                          {t('placement.previousMeasured', {
                            level: profile.overall_level,
                            date: profile.assessed_at !== null ? formatDateDMY(profile.assessed_at) : '—',
                          })}
                        </Typography>
                      )}
                    </Stack>
                  </Alert>
                )}

                <TextField
                  select
                  size="small"
                  label={t('placement.selfLevel')}
                  helperText={t('placement.selfLevelHint')}
                  value={goals.selfLevel}
                  disabled={busy}
                  onChange={(event) =>
                    setGoals({ selfLevel: event.target.value as ProfileLevel | '' })
                  }
                  sx={{ maxWidth: 280 }}
                >
                  <MenuItem value="">{t('placement.selfLevelUnknown')}</MenuItem>
                  {PROFILE_LEVELS.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </TextField>

                <Typography variant="caption" color="text.secondary">
                  {t('placement.goalMovedHint')}
                </Typography>

                <Box>
                  <Button variant="contained" disabled={busy} onClick={() => void submitGoals()}>
                    {busy ? t('common.loading') : t('placement.toExercises')}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        )}

        {/* Вторая и третья ступени: задания блока */}
        {!loading && run?.status === 'in_progress' && run.stage !== 'goals' && (
          <Stack spacing={2}>
            {items.length === 0 && (
              <Alert severity="info">{t('placement.noItems')}</Alert>
            )}

            {items.length > 0 && (
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={(filledCount / items.length) * 100}
                />
                <Typography variant="caption" color="text.secondary">
                  {t('placement.filledOf', { filled: filledCount, total: items.length })}
                </Typography>
              </Box>
            )}

            {items.map((item, index) => (
              <Card key={item.id}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle2" sx={{ flex: 1 }}>
                        {/* Номер по порядку, а не название задания: заголовок
                            иногда выдаёт, о чём задание, и подсказывает */}
                        {t('placement.itemNumber', { number: index + 1, total: items.length })}
                      </Typography>
                      {item.answered
                        ? <Chip size="small" color="success" label={t('placement.answered')} />
                        : isFilled(answers[item.id])
                          ? <Chip size="small" color="primary" variant="outlined" label={t('placement.filled')} />
                          : <Chip size="small" variant="outlined" label={t('placement.notFilled')} />}
                    </Box>

                    {item.exercise.instructions && (
                      <Typography variant="body2" color="text.secondary">
                        {item.exercise.instructions}
                      </Typography>
                    )}

                    <ExerciseRenderer
                      type={item.exercise.type}
                      content={item.exercise.content}
                      answers={answers[item.id] ?? {}}
                      onChange={(next) => setAnswers((current) => ({ ...current, [item.id]: next }))}
                      readOnly={item.answered || busy}
                      // «Соответствия» в тесте собираются на клиенте: ручка
                      // match-attempt для теста закрыта — она отвечала «верно
                      // или нет» на каждую пару и выдавала эталон по биту за
                      // запрос. Без runId рендерер просто соединяет пары, а
                      // проверка идёт на сдаче.
                      runId={
                        item.exercise.type === 'matching'
                          ? undefined
                          : item.exercise_run_id ?? undefined
                      }
                    />

                  </Stack>
                </CardContent>
              </Card>
            ))}

            <Card>
              <CardContent>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {t('placement.blockHint')}
                  </Typography>

                  {/* Одна кнопка на весь блок: задания сдаются разом, потому
                      что сдавать каждое отдельно человек забывает */}
                  <Button
                    variant="contained"
                    disabled={busy}
                    onClick={() =>
                      finishBlock(run.stage === 'adaptive' ? advanceAfterBlock : completeAfterBlock)
                    }
                  >
                    {busy
                      ? t('common.loading')
                      : run.stage === 'adaptive'
                        ? t('placement.answerAndContinue')
                        : t('placement.answerAndFinish')}
                  </Button>

                  <Button
                    color="inherit"
                    disabled={busy}
                    onClick={() => setLeaveOpen(true)}
                  >
                    {t('placement.quit')}
                  </Button>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Typography variant="caption" color="text.secondary">
                  {t('placement.partialHint')}
                </Typography>
              </CardContent>
            </Card>
          </Stack>
        )}
        {/* Выход из теста: цена действия названа, и по умолчанию предлагается
            сохранить — уйти, потеряв решённое, должно быть отдельным выбором,
            а не тем, что происходит само */}
        <Dialog open={leaveOpen} onClose={() => setLeaveOpen(false)} fullWidth maxWidth="xs">
          <DialogTitle>{t('placement.leaveTitle')}</DialogTitle>
          <DialogContent>
            <Typography variant="body2">{t('placement.leaveExplain')}</Typography>
          </DialogContent>
          <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
            <Button color="inherit" disabled={busy} onClick={() => setLeaveOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button color="inherit" disabled={busy} onClick={() => void abandon()}>
              {t('placement.leaveWithoutSaving')}
            </Button>
            <Button variant="contained" disabled={busy} onClick={saveAndLeave}>
              {t('placement.saveResult')}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </DashboardLayout>
  )
}
